import React, { useState } from 'react';
import { useVonChat } from './useVonChat';
import { FilterPanel } from './components/FilterPanel';
import { SessionsList } from './components/SessionsList';
import { ChatArea } from './components/ChatArea';

// Error Boundary Component
class VonChatErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('=== Von Chat Error Boundary Triggered ===');
    console.error('Error:', error);
    console.error('Error Name:', error.name);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Info:', errorInfo);
    console.error('=== End Error Boundary Debug ===');
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 mb-6">
              Von Chat encountered an error. Please refresh the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function VonChat() {
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  
  const hookResult = useVonChat();
  
  // Ensure hook result is available
  if (!hookResult) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Von Chat...</p>
        </div>
      </div>
    );
  }

  const {
    // State
    message,
    filters,
    messages,
    sessions,
    currentSession,
    isLoading,
    isCreatingSession,
    error,
    pagination,
    isLoadingMore,
    
    // Actions
    setMessage,
    updateFilters,
    clearFilters,
    sendMessage,
    createNewSession,
    selectSession,
    loadSessions,
    clearError,
    updateSessionTitle,
    deleteSession,
    loadMoreMessages
  } = hookResult;

  return (
    <VonChatErrorBoundary>
      <div className="h-screen flex bg-gray-50">
        {/* Sessions List - Left Column */}
        <SessionsList
          sessions={sessions || []}
          currentSession={currentSession}
          onSelectSession={selectSession}
          onCreateNewSession={createNewSession}
          isCreatingSession={isCreatingSession || false}
        />

        {/* Chat Area - Center Column */}
        <ChatArea
          currentSession={currentSession}
          messages={messages || []}
          message={message || ''}
          onMessageChange={setMessage}
          onSendMessage={sendMessage}
          isLoading={isLoading || false}
          error={error}
          onDismissError={clearError}
          onUpdateTitle={async (newTitle: string) => {
            if (currentSession) {
              return await updateSessionTitle(currentSession.id, newTitle);
            }
            return false;
          }}
          onDeleteSession={() => {
            if (currentSession) {
              deleteSession(currentSession.id);
            }
          }}
          pagination={pagination}
          isLoadingMore={isLoadingMore || false}
          onLoadMoreMessages={loadMoreMessages}
        />

        {/* Filter Panel - Right Column */}
        <FilterPanel
          filters={filters}
          onFiltersChange={updateFilters}
          isCollapsed={isFiltersCollapsed}
          onToggleCollapse={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
        />
      </div>
    </VonChatErrorBoundary>
  );
} 